import { computed, type Ref, watch } from 'vue'
import { useHttp, useTranslate } from '@/shared/lib'
import { useNotification } from '@/shared/ui'
import { useQuery } from '@tanstack/vue-query'
import type { GlobalSearchQuery } from './type.ts'
import { stripFilterLocalKeys } from './helpers.ts'
import { stripIncompleteConditions } from './filterConditionUtils.ts'

export const useLoadGlobalSearch = (query: Ref<GlobalSearchQuery>) => {
  const http = useHttp()
  const { addError } = useNotification()
  const { trs } = useTranslate()

  /**
   * Формирует единый payload для ключа кэша и тела запроса, чтобы UI-ключи не влияли на API.
   * Недозаполненные условия (без поля/условия/значения) вырезаются здесь же — search-сервис
   * не отклоняет их как ошибку, а трактует как совпадение для любой записи.
   */
  const searchPayload = computed(() => ({
    ...query.value,
    q: query.value.q.trim(),
    object_type_id: Number(query.value.object_type_id),
    filter: stripIncompleteConditions(stripFilterLocalKeys(query.value.filter)),
  }))

  const queryKey = computed(() => ['load_global_search', searchPayload.value])

  /** Вычисляем критерии разрешения поиска */
  const hasSearchCriteria = computed(() => {
    const payload = searchPayload.value

    return (
      payload.q.length > 0 ||
      payload.object_type_id > 0 ||
      payload.filter.flat().length > 0 ||
      Boolean(payload.area)
    )
  })

  const result = useQuery({
    queryKey,
    queryFn: () => http.post('/search', searchPayload.value),
    retry: false,
    enabled: hasSearchCriteria,
  })

  /**
   * Ранее ошибка запроса поиска ничем не показывалась пользователю — таблица
   * просто оставалась пустой (см. `retry: false` без обработчика ошибки).
   * Показываем текст ошибки от сервера, если он есть, иначе — общее сообщение.
   */
  watch(result.error, (error: any) => {
    if (!error) return
    addError(error?.response?.data?.error || error?.response?.data?.data?.msg || trs('request.unknown_error'))
  })

  return result
}
